import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Row, Col } from 'react-grid-system';
import { Button, LoadingSpinner } from '../atoms';
import { useUserLanguage } from '@/customHooks';
import { useQuery, useSubscription, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import textsData from '@/textData/NotificationsTexts.json';
import { Notification } from '@/custom-types';
import { notificationMessageMapper } from '@/helpers';
import { GET_NOTIFICATIONS } from '@/graphql/queries/getNotifications';
import { ON_NEW_NOTIFICATION } from '@/graphql/subscriptions/onNewNotification';
import { MARK_NOTIFICATION_AS_READ } from '@/graphql/mutations/markNotificationAsRead';
import {
  CurrencyOption,
  NotificationTypeOption,
  NotificationOutput,
  IncidentCreatedNotificationData,
  MessageCreatedNotificationData,
  IncidentReportSubmittedNotificationData,
  IncidentFollowUpNotificationData,
  IncidentAssignedNotificationData,
  ContractNotificationData,
} from '@/graphql/generated';

const NotificationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 400px;
  background-color: ${({ theme }) => theme.colors.background2};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.light};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  gap: 0.5rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ClearButton = styled.button`
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: 0.25rem 0.75rem;
  font-size: ${({ theme }) => theme.typography.fontSize.s6};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.background2};
    border-color: ${({ theme }) => theme.colors.primary1};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.s4};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const NotificationsListContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 500px;
`;

const NotificationCard = styled.div<{ $isRead: boolean; $isClickable: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: ${({ theme }) => theme.border.radius.md};
  background-color: ${({ theme, $isRead }) => 
    $isRead ? theme.colors.light : theme.colors.primary1};
  color: ${({ theme, $isRead }) => 
    $isRead ? theme.colors.text : theme.colors.light};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-left: 3px solid ${({ theme, $isRead }) => 
    $isRead ? 'transparent' : theme.colors.secondary1};
  transition: all 0.2s ease;
  cursor: ${({ $isClickable }) => $isClickable ? 'pointer' : 'default'};
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    ${({ $isClickable, $isRead }) => $isClickable && !$isRead ? 'transform: translateX(4px);' : ''}
  }
`;

const NotificationMessage = styled.p`
  margin: 0 0 0.5rem 0;
  word-wrap: break-word;
  font-size: ${({ theme }) => theme.typography.fontSize.s5};
  line-height: 1.4;
`;

const NotificationDate = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.s6};
  opacity: 0.8;
  display: block;
`;

const AdditionalDataContainer = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const AdditionalDataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${({ theme }) => theme.typography.fontSize.s6};
`;

const AdditionalDataLabel = styled.span`
  opacity: 0.8;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const AdditionalDataValue = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.6;
`;

const Footer = styled.div`
  padding: 0.75rem 1rem;
  background-color: ${({ theme }) => theme.colors.light};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const FullWidthButtonWrapper = styled.div`
  width: 100%;
  
  button {
    width: 100%;
  }
`;

export const NotificationsList = ({ onClose, onMarkAllAsReadOnClose }: { 
  onClose: () => void;
  onMarkAllAsReadOnClose?: () => void;
}) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;
  const notificationsEndRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const { data: queryData, loading: isLoading, error: queryError, refetch } = useQuery(GET_NOTIFICATIONS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const { data: subscriptionData } = useSubscription(ON_NEW_NOTIFICATION);

  const [markNotificationAsRead] = useMutation(MARK_NOTIFICATION_AS_READ);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [markingIds, setMarkingIds] = useState<Set<string>>(new Set());
  const [hiddenReadIds, setHiddenReadIds] = useState<Set<string>>(new Set());
  const [expandedNotificationIds, setExpandedNotificationIds] = useState<Set<string>>(new Set());

  const mapAdditionalData = (
    additionalData: NotificationOutput['additionalData'],
  ): Notification['additionalData'] => {
    if (!additionalData) return null;

    return additionalData as unknown as Notification['additionalData'];
  };

  useEffect(() => {
    if (queryData?.userNotifications) {
      const mappedNotifications: Notification[] = queryData.userNotifications.map((notif: NotificationOutput) => {
        const mapped: Notification = {
          id: notif.id,
          type: notif.type,
          message: notif.message,
          createdAt: notif.createdAt,
          read: notif.read,
          additionalData: mapAdditionalData(notif.additionalData),
        };

        return mapped;
      });

      const sortedNotifications = mappedNotifications.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setNotifications(sortedNotifications);
    }
  }, [queryData]);

  useEffect(() => {
    if (subscriptionData?.newNotification) {
      const newNotification = subscriptionData.newNotification;
      const mappedNotification: Notification = {
        id: newNotification.id,
        type: newNotification.type,
        message: newNotification.message,
        createdAt: newNotification.createdAt,
        read: newNotification.read,
        additionalData: mapAdditionalData(newNotification.additionalData),
      };

      setNotifications((prev) => {
        if (prev.some(n => n.id === mappedNotification.id)) {
          return prev;
        }
        const updated = [mappedNotification, ...prev].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        return updated;
      });
    }
  }, [subscriptionData]);

  useEffect(() => {
    notificationsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [notifications]);

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      const markPromises = unreadNotifications.map(notification => 
        markNotificationAsRead({
          variables: { id: notification.id }
        })
      );
      await Promise.all(markPromises);
      await refetch();
      onMarkAllAsReadOnClose?.();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleMarkSingleAsRead = async (notificationId: string) => {
    if (markingIds.has(notificationId)) return;
    
    try {
      setMarkingIds(prev => new Set(prev).add(notificationId));
      await markNotificationAsRead({
        variables: { id: notificationId }
      });
      await refetch();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setMarkingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    const isPaymentDeadline = notification.additionalData?.__typename === 'PaymentDeadlineNotificationData';
    const isIncidentCreated = notification.additionalData?.__typename === 'IncidentCreatedNotificationData';
    const isMessageCreated = notification.additionalData?.__typename === 'MessageCreatedNotificationData';
    const isIncidentAssigned = notification.additionalData?.__typename === 'IncidentAssignedNotificationData';
    const isIncidentReportSubmitted = notification.additionalData?.__typename === 'IncidentReportSubmittedNotificationData';
    const isIncidentFollowUp = notification.additionalData?.__typename === 'IncidentFollowUpNotificationData';
    const isContractNotification = notification.additionalData?.__typename === 'ContractNotificationData';
    const isIncidentResponsibilityRequired =
      notification.type === NotificationTypeOption.IncidentResponsibilityRequired;
    
    if (isPaymentDeadline) {
      setExpandedNotificationIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(notification.id)) {
          newSet.delete(notification.id);
        } else {
          newSet.add(notification.id);
        }
        return newSet;
      });
      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
      return;
    }

    if (isIncidentResponsibilityRequired) {
      const data: any = notification.additionalData;
      const incidentId = data?.incidentId;

      if (incidentId) {
        navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.DETAIL}`, {
          state: { id: incidentId },
        });
      } else {
        navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}`);
      }

      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
      onClose();
      return;
    }
    
    if (isIncidentCreated && notification.additionalData) {
      const incidentData = notification.additionalData as IncidentCreatedNotificationData;
      navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.DETAIL}`, {
        state: { id: incidentData.incidentId }
      });
      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
      onClose();
      return;
    }
    
    if (isIncidentAssigned && notification.additionalData) {
      const incidentData = notification.additionalData as IncidentAssignedNotificationData;
      navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.DETAIL}`, {
        state: { id: incidentData.incidentId }
      });
      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
      onClose();
      return;
    }

    if (isIncidentReportSubmitted && notification.additionalData) {
      const reportData = notification.additionalData as IncidentReportSubmittedNotificationData;
      navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.DETAIL}`, {
        state: { id: reportData.incidentId, selectedTab: '4' }
      });
      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
      onClose();
      return;
    }

    if (isIncidentFollowUp && notification.additionalData) {
      const followUpData = notification.additionalData as IncidentFollowUpNotificationData;
      navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.DETAIL}`, {
        state: { id: followUpData.incidentId }
      });
      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
      onClose();
      return;
    }

    if (isMessageCreated && notification.additionalData) {
      const messageData = notification.additionalData as MessageCreatedNotificationData;
      navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}/${pathURL.DETAIL}`, {
        state: { id: messageData.incidentId }
      });
      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
      onClose();
      return;
    }

    if (isContractNotification && notification.additionalData) {
      const contractData = notification.additionalData as ContractNotificationData;
      navigate(`/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.DETAIL}`, {
        state: { id: contractData.contractId }
      });
      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
      onClose();
      return;
    }
    
    if (notification.type === NotificationTypeOption.NewChatMessage) {
      navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}`);
      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
      onClose();
    } else {
      if (!notification.read) {
        handleMarkSingleAsRead(notification.id);
      }
    }
  };

  const handleClearRead = () => {
    const readNotifications = notifications.filter(n => n.read);
    const readIds = new Set(readNotifications.map(n => n.id));
    setHiddenReadIds(prev => new Set([...prev, ...readIds]));
  };

  const visibleNotifications = notifications.filter(n => !hiddenReadIds.has(n.id));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locales = userLanguage === 'es' ? 'es-ES' : 'en-US';
    
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInHours < 1) {
      const minutes = Math.floor(diffInMs / (1000 * 60));
      return userLanguage === 'es' 
        ? `Hace ${minutes} minuto${minutes !== 1 ? 's' : ''}`
        : `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return userLanguage === 'es'
        ? `Hace ${hours} hora${hours !== 1 ? 's' : ''}`
        : `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      const days = Math.floor(diffInDays);
      return userLanguage === 'es'
        ? `Hace ${days} día${days !== 1 ? 's' : ''}`
        : `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString(locales, {
        day: '2-digit',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  const readableDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const locales = userLanguage === 'es' ? 'es-ES' : 'en-US';
      return date.toLocaleDateString(locales, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  const formatAmount = (amount: string, currency: CurrencyOption, userLanguage: 'es' | 'en') => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return amount;
    const currencyCode = currency === CurrencyOption.Eur ? 'EUR' : currency === CurrencyOption.Usd ? 'USD' : 'USD';
    return new Intl.NumberFormat(userLanguage === 'es' ? 'es-ES' : 'en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
    }).format(numAmount);
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const readCount = notifications.filter(n => n.read && !hiddenReadIds.has(n.id)).length;

  if (isLoading) {
    return (
      <NotificationsContainer>
        <Row justify="center" align="center" style={{ height: '100%', minHeight: '200px' }}>
          <Col xs="content">
            <LoadingSpinner />
          </Col>
        </Row>
      </NotificationsContainer>
    );
  }

  if (queryError && !queryData?.userNotifications) {
    return (
      <NotificationsContainer>
        <EmptyState>
          <p>{texts.error}</p>
        </EmptyState>
      </NotificationsContainer>
    );
  }

  return (
    <NotificationsContainer>
      <Header>
        <Title>{texts.title}</Title>
        <HeaderActions>
          {readCount > 0 && (
            <ClearButton
              onClick={handleClearRead}
              title={texts.clearRead}
            >
              {texts.clearRead}
            </ClearButton>
          )}
          {unreadCount > 0 && (
            <span style={{ 
              backgroundColor: '#C017A2', 
              color: 'white', 
              borderRadius: '50%', 
              width: '24px', 
              height: '24px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {unreadCount}
            </span>
          )}
        </HeaderActions>
      </Header>
      <NotificationsListContainer>
        {visibleNotifications.length === 0 ? (
          <EmptyState>
            <p>{texts.noNotifications}</p>
          </EmptyState>
        ) : (
          visibleNotifications.map((notification) => {
            const isChatMessage = notification.type === NotificationTypeOption.NewChatMessage;
            const isPaymentDeadline =
              notification.additionalData?.__typename === 'PaymentDeadlineNotificationData';
            const isIncidentCreated = notification.additionalData?.__typename === 'IncidentCreatedNotificationData';
            const isMessageCreated = notification.additionalData?.__typename === 'MessageCreatedNotificationData';
            const isIncidentAssigned = notification.additionalData?.__typename === 'IncidentAssignedNotificationData';
            const isIncidentReportSubmitted = notification.additionalData?.__typename === 'IncidentReportSubmittedNotificationData';
            const isIncidentFollowUp =
              notification.additionalData?.__typename === 'IncidentFollowUpNotificationData';
            const isContractNotification = notification.additionalData?.__typename === 'ContractNotificationData';
            const isIncidentResponsibilityRequired =
              notification.type === NotificationTypeOption.IncidentResponsibilityRequired;
            const isClickable =
              isChatMessage ||
              isPaymentDeadline ||
              isIncidentCreated ||
              isMessageCreated ||
              isIncidentAssigned ||
              isIncidentReportSubmitted ||
              isIncidentFollowUp ||
              isContractNotification ||
              isIncidentResponsibilityRequired;
            const isExpanded = expandedNotificationIds.has(notification.id);
            
            return (
              <NotificationCard 
                key={notification.id} 
                $isRead={notification.read}
                $isClickable={isClickable}
                onClick={() => {
                  if (isClickable) {
                    handleNotificationClick(notification);
                  } else if (!notification.read) {
                    handleMarkSingleAsRead(notification.id);
                  }
                }}
              >
                <NotificationMessage>
                  {notificationMessageMapper({
                    type: notification.type,
                    message: notification.message,
                    language: userLanguage,
                    translatedTexts: {
                      newChatMessage: texts.newChatMessage,
                      newChatMessageFrom: texts.newChatMessageFrom,
                      rentChargePaid: texts.rentChargePaid,
                      paymentDeadlineNotification: texts.paymentDeadlineNotification,
                      incidentCreated: texts.incidentCreated,
                      incidentReportSubmitted: texts.incidentReportSubmitted,
                      incidentFollowUp: texts.incidentFollowUp,
                      incidentAssigned: texts.incidentAssigned,
                      contractStarted: texts.contractStarted,
                      contractExpired: texts.contractExpired,
                      contractExpirationSoon: texts.contractExpirationSoon,
                      incidentResponsibilityRequired: texts.incidentResponsibilityRequired,
                    },
                    messageData: notification.additionalData?.__typename === 'MessageCreatedNotificationData' 
                      ? notification.additionalData 
                      : null,
                  })}
                </NotificationMessage>
                {isPaymentDeadline && notification.additionalData && notification.additionalData.__typename === 'PaymentDeadlineNotificationData' && isExpanded && (
                  <AdditionalDataContainer>
                    <AdditionalDataRow>
                      <AdditionalDataLabel>
                        {userLanguage === 'es' ? 'Monto:' : 'Amount:'}
                      </AdditionalDataLabel>
                      <AdditionalDataValue>
                        {formatAmount(
                          notification.additionalData.rentCharge.amountValue,
                          notification.additionalData.rentCharge.currencyOption,
                          userLanguage
                        )}
                      </AdditionalDataValue>
                    </AdditionalDataRow>
                    {notification.additionalData.rentCharge.description && (
                      <AdditionalDataRow>
                        <AdditionalDataLabel>
                          {userLanguage === 'es' ? 'Descripción:' : 'Description:'}
                        </AdditionalDataLabel>
                        <AdditionalDataValue>
                          {notification.additionalData.rentCharge.description}
                        </AdditionalDataValue>
                      </AdditionalDataRow>
                    )}
                    {notification.additionalData.rentCharge.dueDate && (
                      <AdditionalDataRow>
                        <AdditionalDataLabel>
                          {userLanguage === 'es' ? 'Fecha de vencimiento:' : 'Due date:'}
                        </AdditionalDataLabel>
                        <AdditionalDataValue>
                          {readableDate(notification.additionalData.rentCharge.dueDate)}
                        </AdditionalDataValue>
                      </AdditionalDataRow>
                    )}
                  </AdditionalDataContainer>
                )}
                <NotificationDate>{formatDate(notification.createdAt)}</NotificationDate>
              </NotificationCard>
            );
          })
        )}
        <div ref={notificationsEndRef} />
      </NotificationsListContainer>
      {unreadCount > 0 && (
        <Footer>
          <FullWidthButtonWrapper>
            <Button
              onClick={handleMarkAllAsRead}
              isLoading={markingIds.size > 0}
            >
              {texts.markAllAsRead}
            </Button>
          </FullWidthButtonWrapper>
        </Footer>
      )}
    </NotificationsContainer>
  );
};

